<template>
  <form
    class="form-crm-deal-creation"
    v-on:submit.prevent="onSave"
  >
    <v-dialog
      v-model="needsCreatingAdvertisingChannel"
      persistent
      content-class="appointment-creation__popup"
      max-width="fit-content">
      <v-card class="px-5 appointment-creation__popup-wrapper">
        <v-btn
          class="appointment-creation__popup__exit"
          icon
          v-on:click="onCloseCreatingAdvertisingChannelDialog">
          <v-icon color="#192a3e">mdi-close</v-icon>
        </v-btn>
        <v-card-title class="text-h7 justify-center">
          Добавление источника пациента
        </v-card-title>
        <input-text
          v-if="needsCreatingAdvertisingChannel"
          v-bind:required="needsCreatingAdvertisingChannel"
          title="Наименование"
          v-model="customAdvertisingChannel"
          v-letter-capital
          v-bind:violations="customAdvertisingChannelInputErrors" />
        <v-card-actions class="appointment-creation__popup__button-wrapper justify-center">
          <v-btn
            class="appointment-creation__popup__button-cancel"
            color="blue darken-1"
            text
            v-on:click="onCloseCreatingAdvertisingChannelDialog">Отмена</v-btn>
          <v-btn
            class="appointment-creation__popup__button-accept"
            text
            v-on:click="createAdvertisingChannel">Сохранить</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <div class="form-crm-deal-creation__row">
      <the-select
        title="Воронка"
        placeholder="Выберите воронку"
        v-bind:optional="false"
        v-bind:options="funnelOptions"
        v-model="funnel"
        v-on:input="resetStage"
        v-bind:violations="funnelInputErrors" />
      <the-select
        title="Этап"
        placeholder="Выберите этап"
        v-bind:optional="false"
        v-bind:options="funnelStageOptions"
        v-model="stage"
        v-bind:violations="stageInputErrors" />
    </div>
    <input-checkbox
      title="Новый пациент"
      v-if="isAllowedNewPatient"
      v-bind:value="needsCreatingPatient"
      v-on:input="onInputNeedsCreatingPatient" />
    <template v-if="needsCreatingPatient">
      <div class="form-crm-deal-creation__row-patient">
        <input-text
          required="required"
          title="Фамилия"
          v-model="patientSurname"
          v-letter-capital
          v-on:input="onPatientMatchingFieldInput"
          v-bind:violations="surnameInputErrors" />
        <input-text
          required="required"
          title="Имя"
          v-model="patientName"
          v-letter-capital
          v-on:input="onPatientMatchingFieldInput"
          v-bind:violations="nameInputErrors" />
        <input-text
          title="Отчество"
          v-model="patientPatronymic"
          v-letter-capital
          v-on:input="onPatientMatchingFieldInput"
          v-bind:violations="patronymicInputErrors" />
      </div>
      <input-typed-phone
        v-if="isAllowedViewPhone"
        title="Телефон"
        v-bind:type="patientPhoneType"
        v-bind:value="patientPhone"
        v-bind:violations="phoneInputErrors"
        v-on:input="onPatientPhoneInput($event), onPatientMatchingFieldInput()"
      />
      <input-checkbox
        title="Согласен на рассылку"
        v-model="agreeMailing"
        v-on:input="onPatientMatchingFieldInput" />
      <v-expansion-panels
        class="patient-double"
        v-if="matchedPatients.length"
      >
        <v-expansion-panel>
          <v-expansion-panel-header>
            <span class="patient-double__title">
              Внимание! Есть мед.карты с такими данными.
            </span>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <button
              type="button"
              v-for="patient in matchedPatients"
              v-bind:key="`matched-by-name-and-phone${patient.value}`"
              v-on:click="onMatchedPatientSelect(patient.value)"
              class="patient-double__error"
            >
              {{ patient.title }}
              <span
                v-if="patient.is_archived"
                class="patient-double__icon-archive"
                title="Пациент в архиве"
              />
            </button>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
      <input-date
        label="Дата рождения"
        v-model="patientBirthAt"
        v-bind:violations="birthDateInputErrors" />
    </template>
    <autocomplete-input
      item-text="title"
      item-value="value"
      label="ФИО пациента"
      required="required"
      v-bind:menu-props="{ maxWidth: 'min(80%, 586px)' }"
      v-bind:items="patientOptions"
      v-bind:violations="patientInputErrors"
      v-else
      v-model="patient"
      v-on:input="onChangePatient"
      attach
    />
    <autocomplete-input
      item-text="title"
      item-value="value"
      label="Врач"
      v-bind:menu-props="{ maxWidth: 'min(80%, 586px)' }"
      v-bind:items="doctorOptions"
      v-bind:violations="doctorInputErrors"
      v-bind:clearable="true"
      v-model="doctor"
      attach />
    <autocomplete-input
      item-text="title"
      item-value="value"
      label="Услуга"
      v-bind:menu-props="{ maxWidth: 'min(80%, 586px)' }"
      v-bind:items="serviceOptions"
      v-bind:violations="serviceInputErrors"
      v-bind:clearable="true"
      v-model="service"
      attach />
    <autocomplete-input
      item-text="title"
      item-value="value"
      label="Источник пациента"
      v-bind:required="isAdvertisingChannelRequired && needsCreatingPatient"
      v-bind:items="[...sourceOptionsWithDeleted, { title: 'Добавить источник пациента', value: 'new' }]"
      v-bind:clearable="true"
      v-model="source"
      v-bind:violations="sourceInputErrors"
      attach />
    <autocomplete-input
      item-text="title"
      item-value="value"
      label="Канал"
      v-bind:items="channelOptions"
      v-model="channel"
      v-bind:violations="channelInputErrors"
      attach />
    <autocomplete-input
      item-text="title"
      item-value="value"
      label="Причина отказа"
      v-bind:items="[...rejectionReasonOptions, { title: 'Свой вариант', value: 'new' }]"
      v-bind:clearable="true"
      v-model="rejectionReason"
      v-bind:violations="rejectionReasonInputErrors"
      attach />
    <template v-if="needsCreatingRejectionReason">
      <input-text
        v-bind:required="needsCreatingRejectionReason"
        title="Причина отказа"
        v-model="customRejectionReason"
        v-letter-capital
        v-bind:violations="rejectionReasonErrorMessageProcessed" />
    </template>
    <textarea-length-limitation
      placeholder="Примечание к заявке"
      v-bind:max-length="noteMaxLength"
      v-bind:rows="4"
      v-model="note"
      v-bind:violations="noteInputErrors" />
    <button
      class="form-crm-deal-creation__submit"
      v-bind:disabled="isAwaitRequest"
      type="submit"
    >Сохранить</button>
  </form>
</template>
<script>
import AutocompleteInput from '@/components/AutocompleteInput.vue';
import InputCheckbox from '@/components/InputCheckbox.vue';
import InputDate from '@/components/InputDate.vue';
import InputText from '@/components/InputText.vue';
import InputTypedPhone, { PHONE_TYPE_MOBILE } from '@/components/InputTypedPhone.vue';
import TextareaLengthLimitation from '@/components/TextareaLengthLimitation.vue';
import TheSelect from '@/components/TheSelect.vue';
import { getISODateStringByString } from '@/mixins/dateMixin';
import formMeaningfulFieldsMixin from '@/mixins/formMeaningfulFieldsMixin';
import { Maybe } from 'monet';

/**
 * @description Разрешить получение списка ошибок для поля ввода.
 * @param {string} cause Имя поля ввода.
 * @returns {function}
 */
const resolveInputErrors = (cause) => (vm) => (
  cause in vm.violations ? vm.violations[cause] : []
);
/**
 * @description Максимальное количество вводимых символов для поля "Причина отказа".
 * @type {number}
 */
const MAX_LENGTH_REJECTION_REASON = 255;

/**
 * @description Форма создания CRM-сделки.
 * @type {Vue}
 */
export default {
  components: {
    AutocompleteInput,
    InputCheckbox,
    InputDate,
    InputText,
    InputTypedPhone,
    TextareaLengthLimitation,
    TheSelect,
  },
  computed: {
    nameInputErrors: resolveInputErrors('name'),
    patronymicInputErrors: resolveInputErrors('patronymic'),
    surnameInputErrors: resolveInputErrors('surname'),
    phoneInputErrors: resolveInputErrors('phone'),
    patientInputErrors: resolveInputErrors('patient'),
    channelInputErrors: resolveInputErrors('channel'),
    rejectionReasonInputErrors: resolveInputErrors('rejection-reason'),
    customRejectionReasonInputErrors: resolveInputErrors('custom-rejection-reason'),
    customAdvertisingChannelInputErrors: resolveInputErrors('custom-advertising-channel'),
    doctorInputErrors: resolveInputErrors('doctor'),
    funnelInputErrors: resolveInputErrors('funnel'),
    noteInputErrors: resolveInputErrors('note'),
    serviceInputErrors: resolveInputErrors('service'),
    sourceInputErrors: resolveInputErrors('source'),
    stageInputErrors: resolveInputErrors('stage'),
    titleInputErrors: resolveInputErrors('title'),
    birthDateInputErrors: resolveInputErrors('birth-date'),
    /**
     * @description Необходимо создать новую причину отказа.
     * @returns {boolean}
     */
    needsCreatingRejectionReason() {
      return this.rejectionReason === 'new';
    },
    needsCreatingAdvertisingChannel() {
      return this.source === 'new';
    },
    /**
     * @description Получить обработанное сообщение об ошибке.
     * @returns {array}
     */
    rejectionReasonErrorMessageProcessed() {
      const errorMessages = this.customRejectionReasonInputErrors;
      if (errorMessages.length) {
        return this.customRejectionReason.length > MAX_LENGTH_REJECTION_REASON
          ? [`Превышена допустимая длина в ${MAX_LENGTH_REJECTION_REASON} символов`]
          : ['Некорректный формат'];
      }
      return [];
    },
    /**
     * @description Этапы выбранной воронки.
     */
    funnelStageOptions() {
      return this.stageOptions.flatMap(
        (option) => (this.funnel === option.funnel ? [option] : []),
      );
    },
    sourceOptionsWithDeleted() {
      return this.patientSource.fold(this.sourceOptions)((source) => {
        const exists = this.sourceOptions.some(
          ({ value }) => source.value === value,
        );
        if (exists !== true) {
          return [...this.sourceOptions, source];
        }
        return this.sourceOptions;
      });
    },
    /**
     * @description Значимые поля.
     * @type {any}
     */
    meaningfulFields: (vm) => ({
      channel: vm.channel,
      doctor: vm.doctor,
      funnel: vm.funnel,
      note: vm.note,
      patient: vm.patient,
      patientBirthAt: vm.patientBirthAt,
      patientName: vm.patientName,
      patientPatronymic: vm.patientPatronymic,
      patientPhone: vm.patientPhone,
      patientPhoneType: vm.patientPhoneType,
      patientSurname: vm.patientSurname,
      service: vm.service,
      source: vm.source,
      stage: vm.stage,
      agreeMailing: vm.agreeMailing,
    }),
  },
  data() {
    const channel = this.channelOptions.map(({ value }) => value).at(0);
    const [stage] = this
      .stageOptions
      .filter((item) => item.funnel === this.initialFunnel)
      .map((item) => [item.value])
      .at(0);
    return {
      /**
       * @description Согласен на рассылку.
       * @type {boolean}
       */
      agreeMailing: true,
      /**
       * @description Канал.
       * @type {string}
       */
      channel,
      /**
       * @description Врач.
       * @type {string}
       */
      doctor: '',
      /**
       * @description Воронка.
       * @type {string}
       */
      funnel: this.initialFunnel,
      /**
       * @description Необходимо создать нового пациента.
       * @type {boolean}
       */
      needsCreatingPatient: false,
      /**
       * @description Примечание.
       * @type {string}
       */
      note: '',
      /**
       * @description Пациент.
       * @type {string}
       */
      patient: '',
      /**
       * @description Дата рождения пациента.
       * @type {string}
       */
      patientBirthAt: '',
      /**
       * @description Имя пациента.
       * @type {string}
       */
      patientName: '',
      /**
       * @description Отчество пациента.
       * @type {string}
       */
      patientPatronymic: '',
      /**
       * @description Номер телефона пациента.
       * @type {string}
       */
      patientPhone: '',
      /**
       * @description Тип номера телефона пациента.
       * @type {string}
       */
      patientPhoneType: PHONE_TYPE_MOBILE,
      /**
       * @description Фамилия пациента.
       * @type {string}
       */
      patientSurname: '',
      /**
       * @description Услуга.
       * @type {string}
       */
      service: '',
      /**
       * @description Источник.
       * @type {string}
       */
      source: '',
      /**
       * @description Источник пациента.
       * @type {Maybe<{title:string,value:string}>}
       */
      patientSource: Maybe.none(),
      /**
       * @description Этап.
       * @type {string}
       */
      stage,
      /**
       * @description Заголовок.
       * @type {string}
       */
      title: this.initialTitle,
      /**
       * @description Причина отказа.
       * @type {string}
       */
      rejectionReason: '',
      /**
       * @description Своя причина отказа.
       * @type {string}
       */
      customRejectionReason: '',
      /**
       * @description Свой источник пациента.
       * @type {string}
       */
      customAdvertisingChannel: '',
    };
  },
  methods: {
    /**
     * @description Создать источник пацинета.
     * @returns {void}
     */
    createAdvertisingChannel() {
      this.$emit('create-advertising-channel', this.customAdvertisingChannel);
    },
    /**
     * @description Изменение источника.
     * @param {any} patient
     * @returns {void}
     */
    changeSource(patient) {
      if (!patient || !patient.source) {
        this.source = '';
        this.patientSource = Maybe.none();
        return;
      }
      this.source = patient.source;
      const { source, source_name: sourceName } = patient;
      this.patientSource = Maybe.some({ title: sourceName, value: source });
    },
    /**
     * @description Обработать изменение пациента.
     * @param {string} patientId
     * @returns {void}
     */
    onChangePatient(patientId) {
      if (!patientId) {
        this.patient = '';
        return;
      }
      const patient = this.patientOptions.find(({ value }) => value === patientId);
      this.changeSource(patient);
    },
    /**
     * Обработать событие закрытие окна создания канала.
     * @returns {void}
     */
    onCloseCreatingAdvertisingChannelDialog() {
      this.customAdvertisingChannel = '';
      this.source = null;
    },
    /**
     * @description Обработать выбор сопоставленного пациента.
     * @param {string} selectedPatient Выбранный пациент.
     * @returns {void}
     */
    onMatchedPatientSelect(selectedPatient) {
      this.$emit('select-matched-patient');
      this.needsCreatingPatient = false;
      this.patient = String(selectedPatient);
    },
    /**
     * @description Обработать ввод поля пациента для сопоставления.
     * @returns {void}
     */
    onPatientMatchingFieldInput() {
      if (
        (this.patientName !== '' && this.patientSurname !== '')
        || this.patientPhone !== ''
      ) {
        this.$emit('input-matching-field', {
          name: this.patientName,
          patronymic: this.patientPatronymic,
          phone: this.patientPhone,
          surname: this.patientSurname,
        });
      } else {
        this.$emit('input-matching-field', null);
      }
    },
    /**
     * @description Обработать ввод номера телефона пациента.
     * @param {{type:string,value:string}} event
     * @returns {void}
     */
    onPatientPhoneInput({ type, value }) {
      this.patientPhone = value;
      this.patientPhoneType = type;
    },
    /**
     * @description Обработать сохранение.
     * @returns {void}
     */
    onSave() {
      const data = {
        channel: this.channel,
        doctor: this.doctor,
        funnel: this.funnel,
        needsCreatingPatient: this.needsCreatingPatient,
        note: this.note,
        patient: this.patient,
        patientBirthAt: getISODateStringByString(this.patientBirthAt),
        patientName: this.patientName,
        patientPatronymic: this.patientPatronymic,
        patientPhone: this.patientPhone,
        patientPhoneType: this.patientPhoneType,
        patientSurname: this.patientSurname,
        agreeMailing: this.agreeMailing,
        service: this.service,
        source: this.source,
        stage: this.stage,
        title: this.title,
        rejectionReason: this.needsCreatingRejectionReason
          ? this.customRejectionReason
          : this.rejectionReason,
        needsCreatingRejectionReason: this.needsCreatingRejectionReason,
      };
      this.$emit('submit', data);
    },
    /**
     * @description Обработать изменение необходимости создать пациента.
     * @param {boolean} value
     * @returns {void}
     */
    onInputNeedsCreatingPatient(value) {
      this.needsCreatingPatient = value;
      this.onChangePatient('');
    },
    /**
     * @description Сбросить выбор этапа.
     * @returns {void}
     */
    resetStage() {
      this.stage = '';
    },
    /**
     * @description Установить идентификатор источника по названию агрегатора.
     * @returns {void}
     */
    setAdvertisingIdByAggregatorName() {
      const isSelected = this.source !== 'new';
      if (isSelected) { return; }
      if (this.sourceOptions.length && this.customAdvertisingChannel) {
        const aggregatorName = this.customAdvertisingChannel.toLowerCase();
        const candidate = this
          .sourceOptions
          .find((channel) => channel.title.toLowerCase().includes(aggregatorName));
        this.source = candidate?.value || null;
      }
    },
  },
  name: 'FormCrmDealCreation',
  mixins: [formMeaningfulFieldsMixin],
  props: {
    /**
     * @description Обязательность заполнения источника при создании нового пациента.
     */
    isAdvertisingChannelRequired: {
      type: Boolean,
      default: false,
    },
    /**
     * @description Ожидание завершения запроса.
     */
    isAwaitRequest: {
      type: Boolean,
      default: false,
    },
    /**
     * @description Опции каналов.
     */
    channelOptions: {
      required: true,
      type: Array,
    },
    /**
     * @description Опции врачей.
     */
    doctorOptions: {
      required: true,
      type: Array,
    },
    /**
     * @description Опции воронок.
     */
    funnelOptions: {
      required: true,
      type: Array,
    },
    /**
     * @description Изначальный заголовок.
     */
    initialTitle: {
      required: true,
      type: String,
    },
    initialFunnel: {
      required: true,
      type: String,
    },
    /**
     * @description Разрешен новый пациент.
     */
    isAllowedNewPatient: {
      required: true,
      type: Boolean,
    },
    /**
     * @description Разрешен показ телефона.
     */
    isAllowedViewPhone: {
      required: true,
      type: Boolean,
    },
    /**
     * @description Сопоставленные пациенты при создании.
     */
    matchedPatients: {
      required: true,
      type: Array,
    },
    /**
     * @description Максимальная длина примечания.
     */
    noteMaxLength: {
      required: true,
      type: Number,
    },
    /**
     * @description Опции пациентов.
     */
    patientOptions: {
      required: true,
      type: Array,
    },
    /**
     * @description Опции услуг.
     */
    serviceOptions: {
      required: true,
      type: Array,
    },
    /**
     * @description Опции источников.
     */
    sourceOptions: {
      required: true,
      type: Array,
    },
    /**
     * @description Опции этапов.
     */
    stageOptions: {
      required: true,
      type: Array,
    },
    /**
     * @description Максимальная длина заголовка.
     */
    titleMaxLength: {
      required: true,
      type: Number,
    },
    /**
     * @description Ошибки ввода.
     */
    violations: {
      default: () => {},
      type: Object,
    },
    /**
     * @description Опции причин отказа.
     */
    rejectionReasonOptions: {
      required: true,
      type: Array,
    },
  },
  watch: {
    sourceOptions: {
      handler: 'setAdvertisingIdByAggregatorName',
      immediate: true,
    },
  },
};
</script>
<style lang="scss" scoped>
@use "@/scss/buttons";

.form-crm-deal-creation {
  display: flex;
  flex-direction: column;
  gap: .75rem;
  & &__submit {
    @include buttons.btn-blue(10rem, 3rem, 1rem);
    margin-left: auto;
  }
  & &__row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-gap: 10px;
    width: 100%;
    padding-top: 20px;
  }
  & &__row-patient {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    grid-gap: 10px;
    width: 100%;
    padding-top: 20px;
  }
}

.patient-double {
  line-height: 19px;
  margin: 5px 0 0 0;
  & &__title {
    color: var(--warning-color);
  }
  & &__error:hover {
    color: var(--action-color);
  }
  & &__icon-archive {
    display: inline-block;
    width: 16px;
    height: 16px;
    background-image: url("@/assets/img/archive-add-blue.svg");
    background-repeat: no-repeat;
    background-size: contain;
    background-position: center;
  }
}
</style>
